import { gql } from 'urql';
import { PRODUCT } from '../client';
import { ADDRESS_FRAGMENT, ISSUE_FRAGMENT, ORDER_LINE } from './fragments';

export const GET_WAREHOUSES = gql`
  query ${PRODUCT}GetWarehouses {
    warehouses {
      id
      name
      currentNightEnd
      timeZone
      latitude
      longitude
    }
  }
`;

export const GET_STOP_ADDRESS = gql`
${ADDRESS_FRAGMENT}
  query ${PRODUCT}GetStopAddress($input: StopInput!) {
    stop(input: $input) {
      ... on Delivery {
        orders {
          scheduledDeliveryTime {
            earliest
            latest
            timeZone
          }
        }
        location {
          address {
            ...AddressFragment
          }
        }
      }
      ... on PickUp {
        vendor {
          address {
            ...AddressFragment
          }
        }
      }
    }
  }
`;

export const GET_REDELIVERY_STOP_ADDRESS = gql`
${ADDRESS_FRAGMENT}
  query ${PRODUCT}GetRedeliveryStopAddress($input: RedeliveryStopInput!) {
    redeliveryStop(input: $input) {
      location {
        address {
          ...AddressFragment
        }
      }
    }
  }
`;

export const GET_STOP_DELIVERY_DETAILS = gql`
  query ${PRODUCT}GetStopDeliveryDetails($input: StopInput!) {
    stop(input: $input) {
      ... on Delivery {
        reminderMessage
        attachments {
          id
          url
          attachmentCategory
          customerVisible
          qualityStatus
        }
      }
    }
  }
`;

export const GET_STOP_TASKS = gql`
  query ${PRODUCT}GetStopTasks($input: StopInput!) {
    stop(input: $input) {
      ... on Delivery {
        id
        tasks {
          id
          details
          status
          resolvedAt
          driverNote
          createdAt
        }
      }
    }
  }
`;

export const GET_ROUTING_IMPORT_PLAN_CUT_OFF = gql`
  query ${PRODUCT}GetRoutingImportPlanCutOff($routePlanInput: RoutingImportPlanInput!, $cutOffInput: IsPastWarehouseCutOffTimeInput!) {
    routingImportPlan(input: $routePlanInput) {
      id
      status
      optimizationFailureReason
      truckCount
      cohorts {
        color
        id
        name
        truckCount
        enabled
      }
      shiftTruckCounts {
        warehouseShiftId
        shiftType
        startTime
        endTime
        truckCount
      }
    }
    isPastWarehouseCutOffTime (input: $cutOffInput)
  }
`;

// Lean variant of the cutoff-time signal (no routing-import plan), for the
// stage-gate cutoff status where only "has the warehouse cutoff passed" matters.
export const GET_IS_PAST_WAREHOUSE_CUTOFF = gql`
  query ${PRODUCT}GetIsPastWarehouseCutOff($cutOffInput: IsPastWarehouseCutOffTimeInput!) {
    isPastWarehouseCutOffTime(input: $cutOffInput)
  }
`;

export const GET_USERS = gql`
  query ${PRODUCT}GetUsers($role: String!) {
    users(role: $role) {
      id
      name
      email
      payrollId
    }
  }
`;

export const GET_ROUTE_ISSUES = gql`
  ${ISSUE_FRAGMENT}
  ${ORDER_LINE}
  query ${PRODUCT}GetRouteIssues($input: RouteInput!) {
    route(input: $input) {
      driverUser {
        id
        name
      }
      helperDriverUser {
        id
        name
      }
      stops {
        ... on Delivery {
          id
          deliveryStatusDmp
          issues {
            ...IssueFragment
          }
          location {
            id
          }
          orders {
            id
            deliveredAt
            partiallyDelivered
            deliveryStatus
            itemLevelTracking
            erpRecordUrl
            location {
              locationDeliveryWindow {
                startTime
                endTime
              }
              scheduledDeliveryTimeSource
            }
            issues {
              ...IssueFragment
            }
            orderLines {
              ...OrderLineFragment
            }
            vendor {
              name
              legalName
              isWarehouse
            }
          }
          redeliveryStop {
            id
            deliveryStatusDmp
          }
        }
        ... on PickUp {
          id
          deliveryStatusDmp
          issues {
            ...IssueFragment
          }
          orders {
            id
            collectedAt
            deliveryStatus
            erpRecordUrl
            location {
              locationDeliveryWindow {
                startTime
                endTime
              }
              scheduledDeliveryTimeSource
            }
            vendor {
              name
              legalName
              isWarehouse
            }
            issues {
              ...IssueFragment
            }
          }
        }
      }
    }
  }
`;

export const GET_WAREHOUSE_ORDER_CUTOFF_TIME = gql`
  query ${PRODUCT}GetWarehouseOrderCutOffTime($input: StopInput!) {
  stop(input: $input) {
    ... on Delivery {
      orders {
        vendor {
          isWarehouse
        }
        dueAt
      }
    }
  }
}
`;

export const GET_PRIMARY_STOP_STATUSES = gql`
  query ${PRODUCT}GetPrimaryStopStatuses($input: RoutesInput!) {
    routes(input: $input) {
      __typename
      id
      stops {
        __typename
        ... on Delivery {
          __typename
          deliveryStatusDmp
          id
        }
        ... on PickUp {
          __typename
          deliveryStatusDmp
          id
        }
      }
      redeliveryStops {
        id
        deliveryStatusDmp
      }
    }
  }
`;

export const GET_PICKING_LABEL_DATA = gql`
  query ${PRODUCT}GetPickingLabelData($input: RoutesInput!) {
    routes(input: $input) {
      id
      name
      isLobby
      stops {
        ... on Delivery {
          id
          sequenceNumber
          location {
            id
            businessName
          }
          orders {
            id
            vendor {
              isWarehouse
            }
            orderLines {
              id
              orderUnitQuantity
              quantityType
              itemsPerLot
              bulkPickedItem
              sku {
                id
                refrigerationState
                item {
                  name
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_IS_ROUTES_SYNC_ENABLED = gql`
  query ${PRODUCT}GetIsRoutesSyncEnabled($isRoutesSyncEnabledInput: IsRoutesSyncEnabledInput!) {
    isRoutesSyncEnabled(input: $isRoutesSyncEnabledInput)
  }
`;
